import type { Route } from 'next';

/**
 * 作品詳細（コマ選択）ページのURLを組み立てる。
 *
 * アイテムフィルターの状態は productId クエリだけが持っているため、作品詳細へ戻す導線で
 * 引き継ぎ忘れるとフィルターが解除された一覧に戻ってしまう。戻り先URLはこの関数に集約する。
 *
 * ID は 1 始まりのため、0 や NaN（Number() 変換に失敗した値）は未指定として扱いクエリに載せない。
 *
 * @param selectedMaterialId 指定するとコマ詳細モーダルを開いた状態で戻る
 * @param filteredProductId 絞り込み中のアイテムID。未指定ならフィルターなしで戻る
 */
export const buildWorkDetailRoute = ({
  workId,
  selectedMaterialId,
  filteredProductId,
}: {
  workId: number;
  selectedMaterialId?: number;
  filteredProductId?: number;
}): Route => {
  const params = new URLSearchParams();
  if (selectedMaterialId) params.set('selectedMaterialId', String(selectedMaterialId));
  if (filteredProductId) params.set('productId', String(filteredProductId));

  const query = params.toString();
  return (query ? `/works/${workId}?${query}` : `/works/${workId}`) as Route;
};

/**
 * アイテムオプション（商品）ページのURLを組み立てる。
 *
 * 商品ページは、戻り導線でアイテムフィルターを復元するための情報をクエリで受け取る。
 * 呼び出し元ごとに組み立てると引き継ぎ漏れでフィルターが解除されるため、buildWorkDetailRoute と
 * 対になるこの関数に集約する（クエリを増やすときは両方を見る）。
 *
 * @param isFromFilter 絞り込みからコマ詳細モーダルを挟まず直接来た場合に true。パンくずのコマ階層の出し分けに使う
 * @param filteredProductId 絞り込み中のアイテムID。未指定ならフィルターなしで戻る
 */
export const buildProductRoute = ({
  productId,
  materialId,
  workId,
  filteredProductId,
  isFromFilter = false,
}: {
  productId: number;
  materialId: number;
  workId: number;
  filteredProductId?: number;
  isFromFilter?: boolean;
}): Route => {
  const params = new URLSearchParams({ materialId: String(materialId), workId: String(workId) });
  if (filteredProductId) params.set('filteredProductId', String(filteredProductId));
  if (isFromFilter) params.set('fromFilter', 'true');

  return `/product/${productId}?${params.toString()}` as Route;
};

/**
 * searchParams の filteredProductId を検証して数値にする。
 *
 * ユーザーが書き換えられるクエリなので、Number() の結果をそのまま渡すと NaN が下流に流れる。
 * 0 以下・非整数・変換不能はすべて「絞り込みなし」として扱う（ID の前提は buildWorkDetailRoute 参照）。
 *
 * @param rawFilteredProductId - クエリから取得した未検証の文字列
 * @returns 有効な正の整数ならその値。それ以外は undefined
 */
export const parseFilteredProductId = (rawFilteredProductId?: string): number | undefined => {
  const parsed = Number(rawFilteredProductId);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};
